import { MinusCircle } from "lucide-react";
/**
 * The board's status-move dialog (`Led AI - Records Table.dc.html`, "Move
 * status"). A card dropped on a column whose move asks something of the
 * person (`moveRule`) opens this instead of moving:
 *
 *  - **send** — Initiated → Work Required: who it goes to. Procore's
 *    workflow step emails them once the move syncs.
 *  - **done** / **reject** / **reopen** — a comment the move cannot go
 *    without, and for the first two an optional photo.
 *  - **confirm** — Ready to Close / Closed: an optional comment.
 *
 * Confirm writes the status, the picked person and the comment in ONE edit,
 * so the record never sits in a column it was not fully moved into. The
 * comment is kept on the record's history; it is not sent to Procore.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { fetchItemAssignees, type ItemEditDirectoryOption } from "../../api";
import { parseDay } from "../../components/Calendar";
import { DsIcon } from "../../components/DsIcon";
import { RECORD_PHOTO_ACCEPT } from "../../lib/recordPhoto";
import { DS, FONT_MONO, FONT_TEXT, RT, withAlpha } from "../../tokens";
import { PersonAvatar, StatusGlyph } from "./parts";
import { shortDay, type MoveRule, type RecordFacts, type RecordVocab, type StatusDef } from "./recordModel";

export interface MoveFill {
  assignee?: { remoteId?: string; name: string };
  dueDate?: string;
  note?: string;
  photos?: File[];
}

interface Copy {
  heading: string;
  sub: string;
  label?: string;
  placeholder?: string;
  cta: string;
  required: boolean;
  photoLabel?: string;
}

function copyFor(rule: MoveRule, to: StatusDef, noun: string): Copy {
  switch (rule) {
    case "send":
      return { heading: "Send to assignee", sub: "Procore emails the assignee a link to this item and moves it to Work Required.", cta: "Send & move", required: false };
    case "done":
      return { heading: "Mark work complete", sub: `Tell the ${noun} manager what was fixed. They’ll review and accept or send it back.`, label: "What was done?", placeholder: "e.g. Patched and repainted, two coats. Matches adjacent wall.", cta: `Move to ${to.label}`, required: true, photoLabel: "Add after photo" };
    case "reject":
      return { heading: "Send back to the sub", sub: "Your comment is kept on the item’s history with the move.", label: "What still needs fixing?", placeholder: "e.g. Paint is still patchy at the ceiling line — needs a third coat.", cta: "Send back", required: true, photoLabel: "Add photo" };
    case "reopen":
      return { heading: "Reopen this item", sub: "Closed items need a reason to reopen. It’s recorded on the item’s history.", label: "Why reopen?", placeholder: "e.g. Crack has come back after the paint cured.", cta: "Reopen", required: true };
    default:
      return to.key === "closed"
        ? { heading: "Close this item", sub: "Final sign-off. The item closes in Procore and drops off open lists.", label: "Comment", placeholder: "Optional", cta: "Close item", required: false }
        : { heading: "Approve the work", sub: "Passes to the final approver for sign-off.", label: "Comment", placeholder: "Optional", cta: `Move to ${to.label}`, required: false };
  }
}

const MAX_PHOTOS = 4;

export function MoveRecordModal({
  f,
  vocab,
  from,
  to,
  rule,
  search,
  onCancel,
  onMove,
}: {
  f: RecordFacts;
  vocab: RecordVocab;
  from: StatusDef;
  to: StatusDef;
  rule: Exclude<MoveRule, "none">;
  search: string;
  onCancel: () => void;
  /** Resolves to an error to show, or null once the move is saved. */
  onMove: (fill: MoveFill) => Promise<string | null>;
}) {
  const copy = copyFor(rule, to, vocab === "punch" ? "punch item" : "observation");
  const isSend = rule === "send";
  const [text, setText] = useState("");
  const [pick, setPick] = useState<{ remoteId?: string; name: string } | null>(null);
  const [due, setDue] = useState("");
  const [photos, setPhotos] = useState<Array<{ file: File; preview: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  useEffect(() => () => photos.forEach((photo) => URL.revokeObjectURL(photo.preview)), [photos]);

  // ---- who it goes to (send) ------------------------------------------------
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState<ItemEditDirectoryOption[]>([]);
  const [peopleState, setPeopleState] = useState<"loading" | "ready" | string>("loading");
  useEffect(() => {
    if (!isSend) return;
    let live = true;
    const term = query.trim();
    setPeopleState("loading");
    const timer = setTimeout(() => {
      fetchItemAssignees(f.row.jobSiteId, vocab === "punch" ? "punch" : "observations", term, search, f.row.visitId || null)
        .then((result) => {
          if (!live) return;
          setPeople(result);
          setPeopleState("ready");
          // The record's own assignee is the default choice, when the
          // directory knows them.
          if (!term && f.assignee) {
            const own = result.find((person) => person.name.toLowerCase() === f.assignee!.toLowerCase());
            if (own) setPick((current) => current ?? { remoteId: own.remoteId, name: own.name });
          }
        })
        .catch((failure: Error) => { if (live) { setPeople([]); setPeopleState(failure.message || "The project's people are unavailable."); } });
    }, term ? 220 : 0);
    return () => { live = false; clearTimeout(timer); };
  }, [isSend, query, f.row.jobSiteId, f.row.visitId, f.assignee, vocab, search]);
  const chips = useMemo(() => {
    const shown = people.slice(0, 6);
    if (pick && !shown.some((person) => person.remoteId === pick.remoteId)) {
      const picked = people.find((person) => person.remoteId === pick.remoteId);
      if (picked) shown.unshift(picked);
    }
    return shown.slice(0, 6);
  }, [people, pick]);

  const ok = (isSend ? !!pick : true) && (!copy.required || !!text.trim()) && !busy;

  async function confirm() {
    if (!ok) return;
    setBusy(true);
    setError(null);
    const failure = await onMove({
      ...(isSend && pick ? { assignee: pick } : {}),
      ...(due ? { dueDate: due } : {}),
      ...(text.trim() ? { note: text.trim() } : {}),
      ...(photos.length ? { photos: photos.map((photo) => photo.file) } : {}),
    });
    setBusy(false);
    if (failure) setError(failure);
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
      if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) void confirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    const chosen = [...files].filter((file) => RECORD_PHOTO_ACCEPT.split(",").includes(file.type)).slice(0, MAX_PHOTOS - photos.length);
    setPhotos((current) => [...current, ...chosen.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
  };

  const num = f.number ? `#${f.number}` : "";
  const dueDay = f.due ?? (due ? parseDay(due) : null);
  // Whose move it is next, by ROLE (the status's own `ball`) — never the
  // record's stored holder string, which the console no longer shows.
  const ballNext = to.ball || "nobody — closed";

  return createPortal(
    <div
      data-testid="move-record-scrim"
      className="rt-fade"
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: RT.scrim, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "96px 24px 24px", overflowY: "auto", fontFamily: FONT_TEXT }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={copy.heading}
        data-testid="move-record"
        data-rule={rule}
        data-to={to.key}
        data-screen-label="Move status"
        onClick={(event) => event.stopPropagation()}
        style={{ width: 520, maxWidth: "100%", background: RT.pane, borderRadius: 16, boxShadow: RT.shadowModal, overflow: "hidden", color: DS.ink }}
      >
        {/* the record */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 18px 0" }}>
          {num && <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: DS.meta }}>{num}</span>}
          <span data-testid="move-record-title" style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.row.title}</span>
          <button
            type="button"
            className="rt-hover-ink"
            aria-label="Close"
            onClick={onCancel}
            style={{ width: 28, height: 28, borderRadius: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", border: "none", background: "transparent", cursor: "pointer", color: DS.body2, padding: 0 }}
          >
            <DsIcon name="close" size={13} color="currentColor" />
          </button>
        </div>

        {/* from → to */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 18px 0" }}>
          <span data-testid="move-record-from" style={{ ...STATUS_CHIP, border: "1px solid rgba(10,10,10,0.1)", fontWeight: 600, color: DS.body2 }}>
            <StatusGlyph status={from} size={14} />
            {from.label}
          </span>
          <DsIcon name="arrowRight" size={14} color={DS.meta} />
          <span data-testid="move-record-to" style={{ ...STATUS_CHIP, background: withAlpha(to.color, 0.1), fontWeight: 700 }}>
            <StatusGlyph status={to} size={14} />
            {to.label}
          </span>
        </div>

        <div style={{ padding: "16px 18px 4px" }}>
          <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.4px" }}>{copy.heading}</div>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: DS.body2, marginTop: 4, textWrap: "pretty" } as CSSProperties}>{copy.sub}</div>

          {isSend ? (
            <div style={{ marginTop: 14, border: "1px solid rgba(10,10,10,0.1)", borderRadius: 12, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 42, padding: "6px 12px", borderBottom: "1px solid rgba(10,10,10,0.07)" }}>
                <span style={{ flex: "none", width: 30, fontSize: 12.5, color: DS.meta }}>To</span>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                  {chips.map((person) => {
                    const on = pick?.remoteId === person.remoteId;
                    return (
                      <button
                        key={person.remoteId}
                        type="button"
                        data-testid="move-record-person"
                        aria-pressed={on}
                        title={person.company ? `${person.name} · ${person.company}` : person.name}
                        onClick={() => setPick({ remoteId: person.remoteId, name: person.name })}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px 0 4px", borderRadius: 14, border: `1px solid ${on ? RT.pickOnLine : "rgba(10,10,10,0.12)"}`, background: on ? RT.pickOn : RT.pane, font: "inherit", fontSize: 12.5, fontWeight: on ? 700 : 500, color: DS.ink, cursor: "pointer", whiteSpace: "nowrap" }}
                      >
                        <PersonAvatar name={person.name} size={20} />
                        {person.name}
                      </button>
                    );
                  })}
                  <input
                    className="rt-input"
                    data-testid="move-record-people-search"
                    value={query}
                    onInput={(event) => setQuery(event.currentTarget.value)}
                    placeholder={chips.length ? "Someone else…" : "Search people…"}
                    aria-label="Search people"
                    style={{ flex: "1 0 110px", minWidth: 110, height: 28, border: 0, outline: "none", background: "transparent", fontFamily: FONT_TEXT, fontSize: 12.5, color: DS.ink, padding: "0 4px" }}
                  />
                  {peopleState !== "ready" && peopleState !== "loading" && <span style={{ width: "100%", fontSize: 12, color: DS.red }}>{peopleState}</span>}
                  {peopleState === "ready" && chips.length === 0 && <span style={{ fontSize: 12, color: DS.meta }}>No one matches</span>}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, height: 40, padding: "0 12px", borderBottom: "1px solid rgba(10,10,10,0.07)", fontSize: 13 }}>
                <span style={{ flex: "none", width: 30, fontSize: 12.5, color: DS.meta }}>Re</span>
                <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{`Punch item ${num ? `${num} · ` : ""}${f.row.title}`}</span>
              </div>
              <textarea
                data-testid="move-record-text"
                value={text}
                onInput={(event) => setText(event.currentTarget.value)}
                placeholder="Add a note for the item’s history (optional)"
                rows={3}
                style={{ display: "block", width: "100%", boxSizing: "border-box", resize: "none", border: 0, outline: "none", padding: "10px 12px", fontFamily: FONT_TEXT, fontSize: 13.5, lineHeight: 1.5, color: DS.ink, background: RT.pane }}
              />
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, padding: "9px 12px", background: RT.strip, borderTop: "1px solid rgba(10,10,10,0.07)", fontSize: 12, color: DS.body2 }}>
                <DsIcon name="calendar" size={12} color={DS.meta} />
                {f.due ? (
                  <span>{`Due ${shortDay(f.due)}, ${f.due.getFullYear()}`}</span>
                ) : (
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    {dueDay ? `Due ${shortDay(dueDay)}, ${dueDay.getFullYear()}` : "Add due date"}
                    <input type="date" data-testid="move-record-due" aria-label="Due date" value={due} onChange={(event) => setDue(event.target.value)} style={{ height: 22, border: "1px solid rgba(10,10,10,0.12)", borderRadius: 6, padding: "0 6px", fontFamily: FONT_TEXT, fontSize: 11.5, color: DS.ink, background: RT.pane }} />
                  </label>
                )}
                <span style={{ color: RT.dotSep }}>·</span>
                <DsIcon name="pin" size={12} color={DS.meta} />
                <span>{f.locShort ?? "No location"}</span>
                <span style={{ color: RT.dotSep }}>·</span>
                <span>{`${f.photos} photo${f.photos === 1 ? "" : "s"} attached`}</span>
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: RT.cell, marginTop: 14 }}>
                {copy.label}
                {copy.required && <span style={{ color: DS.red }}> *</span>}
              </div>
              <textarea
                data-testid="move-record-text"
                value={text}
                onInput={(event) => setText(event.currentTarget.value)}
                placeholder={copy.placeholder}
                rows={4}
                autoFocus
                style={{ display: "block", width: "100%", boxSizing: "border-box", marginTop: 6, resize: "vertical", border: `1px solid ${text.trim() || !copy.required ? "rgba(10,10,10,0.14)" : RT.needed}`, borderRadius: 10, padding: "10px 12px", fontFamily: FONT_TEXT, fontSize: 13.5, lineHeight: 1.5, color: DS.ink, outline: "none", background: RT.pane }}
              />
              {copy.photoLabel && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                  {photos.map((photo, index) => (
                    <span key={photo.preview} data-testid="move-record-photo" style={{ position: "relative", width: 48, height: 64, borderRadius: 7, overflow: "hidden", background: RT.photoBg, display: "block", flex: "none" }}>
                      <img src={photo.preview} alt={photo.file.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      <button
                        type="button"
                        aria-label={`Remove ${photo.file.name}`}
                        onClick={() => setPhotos((current) => current.filter((_, k) => k !== index))}
                        style={{ position: "absolute", right: 2, top: 2, width: 16, height: 16, borderRadius: 8, background: "rgba(10,10,10,0.6)", border: "none", padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                      >
                        <MinusCircle size={14} color={RT.pane} aria-hidden />
                      </button>
                    </span>
                  ))}
                  <input ref={fileInput} type="file" accept={RECORD_PHOTO_ACCEPT} multiple hidden onChange={(event) => { addPhotos(event.target.files); event.target.value = ""; }} />
                  {photos.length < MAX_PHOTOS && (
                    <button
                      type="button"
                      className="rt-hover"
                      data-testid="move-record-add-photo"
                      onClick={() => fileInput.current?.click()}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 32, padding: "0 11px", borderRadius: 8, border: "1px dashed rgba(10,10,10,0.2)", background: "transparent", font: "inherit", fontSize: 12.5, fontWeight: 600, color: DS.body2, cursor: "pointer" }}
                    >
                      <DsIcon name="camera" size={13} color="currentColor" />
                      {copy.photoLabel}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px 16px", marginTop: 10, borderTop: "1px solid rgba(10,10,10,0.07)", background: RT.sheetFoot }}>
          <span data-testid="move-record-note" title={error ?? undefined} style={{ flex: 1, minWidth: 0, display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, lineHeight: 1.35, color: error ? DS.red : DS.body2 }}>
            {error ?? (
              <>
                <DsIcon name="person" size={12} color={DS.meta} style={{ flex: "none" }} />
                <span>{`Next up → ${ballNext}`}</span>
              </>
            )}
          </span>
          <FootButton onClick={onCancel}>Cancel</FootButton>
          <FootButton primary disabled={!ok} testId="move-record-confirm" onClick={() => void confirm()}>
            {isSend && <DsIcon name="send" size={12} color={RT.sendGlyph} />}
            {busy ? "Saving…" : copy.cta}
          </FootButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const STATUS_CHIP: CSSProperties = {
  flex: "none",
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  height: 30,
  padding: "0 11px 0 9px",
  borderRadius: 15,
  fontSize: 13,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
};

function FootButton({ children, onClick, primary = false, disabled = false, testId }: { children: ReactNode; onClick: () => void; primary?: boolean; disabled?: boolean; testId?: string }) {
  return (
    <button
      type="button"
      className={primary ? undefined : "rt-hover"}
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      style={{
        flex: "none",
        height: 36,
        padding: primary ? "0 16px" : "0 14px",
        borderRadius: 9,
        border: primary ? "none" : "1px solid rgba(10,10,10,0.12)",
        background: primary ? DS.ink : RT.pane,
        color: primary ? RT.pane : DS.ink,
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        font: "inherit",
        fontSize: 13,
        fontWeight: primary ? 700 : 600,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}
